/**
 * Hover tooltips for the UiKit ToolTip component.
 *
 * The bubble is a [popover], so it renders in the top layer and escapes overflow
 * clipping in tables and side panels. It is placed here rather than by CSS anchor
 * positioning: anchor positioning ignores a sticky anchor's paint-time offset, and
 * every table pins its actions column with `position: sticky` (.table-row--actions),
 * which put the bubble at the cell's unscrolled position on any table wide enough to
 * scroll sideways. A client rect reports where the target really is — sticky offsets,
 * scroll containers and all — so the placement is measured from that.
 */

// Minimum distance the caret's centre keeps from the bubble's left/right edge, so
// it can never land on a rounded corner: --radius-sm (4px) + the caret's half-width
// (5px), rounded up.
const CARET_INSET = 10;

// Gap between the bubble and the element it points at, and the smallest distance it
// keeps from the viewport edges. Matches --space-2 / --space-3.
const GAP = 8;
const VIEWPORT_MARGIN = 12;

/**
 * Resolves the element the popover anchors to and hover is tracked on.
 * The ToolTip wrapper uses `display: contents` (no box of its own), in which case it
 * can't be a hover target or popover anchor — descend to its real content element.
 *
 * Descends rather than taking the first child outright: that child can be layout-
 * transparent too (a component that wraps its own content in something with
 * `display: contents`), and an element with no box has no rect to anchor to — the
 * popover then silently falls back to the UA's inset placement, landing in the same
 * spot for every instance on the page.
 * @param {HTMLElement} target - The wrapper element passed from Blazor
 * @returns {HTMLElement}
 */
function resolveHost(target) {
    let host = target;

    while (getComputedStyle(host).display === 'contents' && host.firstElementChild) {
        host = host.firstElementChild;
    }

    return host;
}

/**
 * Places the bubble against the target and points the caret at it. Must run after the
 * popover is shown, so the bubble has been laid out and can be measured.
 * @param {HTMLElement} host - The element the bubble points at
 * @param {HTMLElement} tooltip - The popover element
 */
function place(host, tooltip) {
    // Client rects, so the target's real on-screen position is used: it may sit in a
    // sticky cell, inside a scrolled container, or both.
    const hostRect = host.getBoundingClientRect();
    const tipRect = tooltip.getBoundingClientRect();

    // Above the target by preference, below it when there isn't room. The caret is a
    // pseudo-element and can't move itself, so a class tells it which way to point.
    const placedBelow = hostRect.top - tipRect.height - GAP < VIEWPORT_MARGIN;
    const top = placedBelow ? hostRect.bottom + GAP : hostRect.top - tipRect.height - GAP;
    tooltip.classList.toggle('tooltip--below', placedBelow);

    // Centred on the target, then kept inside the viewport — near an edge the bubble's
    // centre is then no longer the target's, so the caret is positioned separately.
    const centredLeft = hostRect.left + hostRect.width / 2 - tipRect.width / 2;
    const maxLeft = window.innerWidth - tipRect.width - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(centredLeft, maxLeft));

    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.top = `${Math.round(top)}px`;

    // Point the caret at the target rather than at the bubble's middle, clamped away
    // from the rounded corners.
    const caretX = hostRect.left + hostRect.width / 2 - left;
    const clampedCaretX = Math.max(CARET_INSET, Math.min(caretX, tipRect.width - CARET_INSET));
    tooltip.style.setProperty('--tooltip-caret-x', `${clampedCaretX}px`);
}

/**
 * Shows the tooltip while the target is hovered.
 * @param {HTMLElement} target - The ToolTip wrapper element
 * @param {HTMLElement} tooltip - The popover element holding the tooltip text
 */
function addListeners(target, tooltip) {
    if (!target || !tooltip) return;

    const host = resolveHost(target);

    const show = () => {
        tooltip.showPopover();
        place(host, tooltip);
    };
    const hide = () => tooltip.hidePopover();

    host.addEventListener('mouseenter', show);
    host.addEventListener('mouseleave', hide);
    target._tooltipListeners = { host, show, hide };
}

/**
 * Detaches the hover listeners added by addListeners. Called when the component is
 * disposed, so the host element doesn't keep handlers to a removed popover.
 * @param {HTMLElement} target - The ToolTip wrapper element
 */
function removeListeners(target) {
    if (!target?._tooltipListeners) return;

    const { host, show, hide } = target._tooltipListeners;
    host.removeEventListener('mouseenter', show);
    host.removeEventListener('mouseleave', hide);
    delete target._tooltipListeners;
}

globalThis.tooltip = {
    addListeners,
    removeListeners
};
